import React, { useState, useEffect } from 'react';
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer
} from 'recharts';
import styles from './EventStatsChart.module.css';

interface StatsProps {
  stats: {
    viewCount: number;
    uniqueViewCount: number;
    interestedCount: number;
    savedCount: number;
  };
}

const EventStatsChart: React.FC<StatsProps> = ({ stats }) => {
  // État pour détecter si on est sur mobile
  const [isMobile, setIsMobile] = useState<boolean>(window.innerWidth <= 768);
  
  // Détection de la taille de l'écran
  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth <= 768);
    };
    
    window.addEventListener('resize', handleResize);
    return () => {
      window.removeEventListener('resize', handleResize);
    };
  }, []);

  const data = [
    {
      name: 'Vues',
      value: stats.viewCount,
    },
    {
      name: 'Intéressés',
      value: stats.interestedCount,
    },
    {
      name: 'Enregistrés',
      value: stats.savedCount,
    },
  ];

  // Afficher un résumé des statistiques en plus du graphique
  return (
    <div className={styles.statsContainer}>
      <div className={styles.summary}>
        <div className={styles.statItem}>
          <div className={styles.statValue}>{stats.viewCount}</div>
          <div className={styles.statLabel}>Vues</div>
        </div>
        <div className={styles.statItem}>
          <div className={styles.statValue}>{stats.interestedCount}</div>
          <div className={styles.statLabel}>Intéressés</div>
        </div>
        <div className={styles.statItem}>
          <div className={styles.statValue}>{stats.savedCount}</div>
          <div className={styles.statLabel}>Enregistrés</div>
        </div>
      </div>
      
      <div className={styles.chartContainer}>
        <ResponsiveContainer width="100%" height={isMobile ? 200 : 300}>
          <BarChart
            data={data}
            margin={{
              top: isMobile ? 10 : 20,
              right: isMobile ? 10 : 30,
              left: isMobile ? 0 : 20,
              bottom: isMobile ? 5 : 5,
            }}
            barCategoryGap={isMobile ? "20%" : "35%"}
            barGap={0}
            layout="vertical"
          >
            <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" horizontal={true} vertical={false} />
            <XAxis 
              type="number"
              tick={{ fontSize: isMobile ? 10 : 12, fill: '#64748b' }}
              tickMargin={isMobile ? 5 : 10}
              axisLine={{ stroke: '#cbd5e1' }}
            />
            <YAxis 
              type="category"
              dataKey="name"
              tick={{ fontSize: isMobile ? 10 : 12, fill: '#64748b' }}
              width={isMobile ? 70 : 90}
              axisLine={{ stroke: '#cbd5e1' }}
            />
            <Tooltip 
              contentStyle={{ 
                backgroundColor: 'rgba(255, 255, 255, 0.95)',
                border: '1px solid #e2e8f0',
                borderRadius: '8px',
                boxShadow: '0 4px 6px rgba(0, 0, 0, 0.1)',
                fontSize: isMobile ? '10px' : '12px',
                padding: isMobile ? '4px 8px' : '8px 10px'
              }}
              itemStyle={{ fontSize: isMobile ? '10px' : '12px' }}
              formatter={(value) => [`${value}`, 'Total']}
              labelFormatter={(label) => `${label}`}
            />
            <Legend 
              wrapperStyle={{ 
                paddingTop: isMobile ? 5 : 10,
                fontSize: isMobile ? '10px' : '12px'
              }}
              iconSize={isMobile ? 8 : 10}
              align="center"
              verticalAlign="bottom"
              layout="horizontal"
            />
            <Bar 
              dataKey="value" 
              name="Total" 
              fill="#4F46E5" 
              radius={[0, 6, 6, 0]} 
              animationDuration={1500}
              animationEasing="ease"
            />
          </BarChart>
        </ResponsiveContainer>
      </div>
    </div>
  );
};

export default EventStatsChart;
